import React from "react";
import { InlineMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";

function randInt(min, max, excluded = new Set()) {
  let value;
  do {
    value = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (excluded.has(value));
  return value;
}

function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
}

export function generateTask() {
  const isFunction = Math.random() < 0.5;
  const argumentCount = randInt(4, 6);
  const argumentsSet = new Set();

  while (argumentsSet.size < argumentCount) {
    argumentsSet.add(randInt(-7, 7));
  }

  const pairs = [...argumentsSet].map((x) => ({
    x,
    y: randInt(-9, 9),
  }));

  let repeatedArgument = null;
  if (!isFunction) {
    const source = pairs[randInt(0, pairs.length - 1)];
    repeatedArgument = source.x;
    pairs.push({
      x: source.x,
      y: randInt(-9, 9, new Set([source.y])),
    });
  }

  return {
    type: "IsRelationFunctionTask",
    data: {
      pairs: shuffle(pairs),
      isFunction,
      repeatedArgument,
    },
  };
}

function PairTable({ pairs }) {
  const cellStyle = {
    border: "1px solid currentColor",
    padding: "8px 12px",
    textAlign: "center",
    minWidth: 46,
  };

  return (
    <div style={{ overflowX: "auto", marginTop: 10 }}>
      <table style={{ borderCollapse: "collapse", margin: "0 auto" }}>
        <tbody>
          <tr>
            <td style={{ ...cellStyle, fontWeight: 800 }}><InlineMath math="x" /></td>
            {pairs.map((pair, index) => (
              <td key={`x-${index}`} style={cellStyle}>{pair.x}</td>
            ))}
          </tr>
          <tr>
            <td style={{ ...cellStyle, fontWeight: 800 }}><InlineMath math="y" /></td>
            {pairs.map((pair, index) => (
              <td key={`y-${index}`} style={cellStyle}>{pair.y}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div>Przyporządkowanie przedstawiono w tabeli:</div>
      <PairTable pairs={data.pairs} />
      <div style={{ marginTop: 10 }}>Czy to przyporządkowanie jest funkcją?</div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = typeof userAnswer === "string" ? userAnswer : (userAnswer?.value || "");
  return (
    <div style={{ marginTop: 10, maxWidth: 220 }}>
      <SelectInput
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        options={[
          { value: "TAK", label: "TAK" },
          { value: "NIE", label: "NIE" },
        ]}
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const answer = typeof userAnswer === "string" ? userAnswer : userAnswer?.value;
  return answer === (data.isFunction ? "TAK" : "NIE");
}

export function renderAnswer({ data }) {
  return <strong>{data.isFunction ? "TAK" : "NIE"}</strong>;
}

export function renderExplanation({ data }) {
  if (data.isFunction) {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        Każdemu argumentowi <InlineMath math="x" /> przyporządkowano dokładnie jedną
        wartość <InlineMath math="y" />. Różne argumenty mogą mieć tę samą wartość,
        dlatego to przyporządkowanie jest funkcją.
      </div>
    );
  }

  const values = data.pairs
    .filter((pair) => pair.x === data.repeatedArgument)
    .map((pair) => pair.y);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      Argumentowi <InlineMath math={`x=${data.repeatedArgument}`} /> przyporządkowano
      dwie różne wartości: <InlineMath math={`${values[0]}`} /> oraz{" "}
      <InlineMath math={`${values[1]}`} />. Jeden argument nie może mieć dwóch różnych
      wartości funkcji, więc to przyporządkowanie nie jest funkcją.
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const answer = typeof userAnswer === "string" ? userAnswer : userAnswer?.value;
  return answer ? <strong>{answer}</strong> : <i>(brak odpowiedzi)</i>;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
